import React, { useEffect, useState } from 'react';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import type { ProgressSummary } from '../../Shared/types';

const Progress: React.FC = () => {
  const [progress, setProgress] = useState<ProgressSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadProgress();
  }, []);

  const loadProgress = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiClient.getProgressSummary();
      setProgress(data);
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string): JSX.Element => {
    const styles = {
      locked: 'bg-gray-100 text-gray-800',
      current: 'bg-blue-100 text-blue-800',
      mastered: 'bg-green-100 text-green-800',
    };
    const icons = {
      locked: '🔒',
      current: '📖',
      mastered: '✅',
    };
    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          styles[status as keyof typeof styles] || styles.locked
        }`}
      >
        {icons[status as keyof typeof icons]} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <Navbar>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Progress 📊</h1>
          <p className="text-lg text-gray-600">Track your learning journey and achievements</p>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <Card>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => void loadProgress()} variant="secondary">
              Try Again
            </Button>
          </Card>
        ) : progress ? (
          <div>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="text-center">
                <p className="text-sm text-gray-600 mb-2">Total Worksheets</p>
                <p className="text-4xl font-bold text-blue-600">
                  {progress.total_worksheets_completed}
                </p>
              </Card>
              <Card className="text-center">
                <p className="text-sm text-gray-600 mb-2">Topics Mastered</p>
                <p className="text-4xl font-bold text-green-600">
                  {progress.topics.filter((t) => t.status === 'mastered').length}
                </p>
              </Card>
              <Card className="text-center">
                <p className="text-sm text-gray-600 mb-2">Current Topic</p>
                <p className="text-xl font-semibold text-gray-900 mt-3">
                  {progress.current_topic?.name || 'None'}
                </p>
              </Card>
            </div>

            {/* Current Topic Detail */}
            {progress.current_topic && (
              <Card className="mb-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {progress.current_topic.name}
                    </h2>
                    <p className="text-gray-600">{progress.current_topic.description}</p>
                  </div>
                  {progress.current_topic.status && getStatusBadge(progress.current_topic.status)}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Worksheets Completed</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {progress.current_topic.worksheets_completed}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Current Pack</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {progress.current_topic.current_pack || 1}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Avg Score</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {typeof progress.current_topic.average_score === 'number'
                        ? `${progress.current_topic.average_score.toFixed(1)}%`
                        : '0%'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Mastery Level</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {progress.current_topic.mastery_level || 0}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* All Topics List */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">All Topics</h2>
              <div className="space-y-4">
                {progress.topics.map((topic) => (
                  <Card key={topic.id} className="hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{topic.name}</h3>
                          {topic.status && getStatusBadge(topic.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{topic.description}</p>
                        <div className="flex items-center gap-6 text-sm text-gray-700">
                          <span>
                            📝 {topic.worksheets_completed} worksheet
                            {topic.worksheets_completed !== 1 ? 's' : ''}
                          </span>
                          {typeof topic.average_score === 'number' && (
                            <span>📊 {topic.average_score.toFixed(1)}% avg</span>
                          )}
                          {topic.status === 'current' && topic.current_pack && (
                            <span>📦 Pack {topic.current_pack}</span>
                          )}
                          {topic.status === 'mastered' && topic.mastery_level && (
                            <span>⭐ Level {topic.mastery_level}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Card>
            <p className="text-gray-600 text-center">No progress data available</p>
          </Card>
        )}
      </div>
    </Navbar>
  );
};

export default Progress;
